/**
 * The invite deep link. The SMS/email invitation NOW links to
 * PUBLIC_BASE_URL/public/invite?token=… — an api-served page (src/household/pages.ts),
 * because /invite resolved only while Caddy sat in front splitting page paths
 * from api paths, and on the ALB an invitee's first contact with the product
 * was a JSON 404. THIS FILE STILL MATCHES THE OLD PATH, deliberately: links
 * already delivered keep arriving for one INVITE_TTL_DAYS window, and Caddy
 * still serves them. Delete the /invite match once that window has passed.
 * main.tsx stashes the token here before React
 * mounts and rewrites the URL to #/invites, so the visitor walks sign-in (and
 * onboarding, if they are new) and lands on the invites screen with the token
 * already filled in. Before this, the link opened the app to a sign-in screen
 * that forgot why the person came, and they retyped the 43-character token by
 * hand.
 *
 * ITS OWN STORAGE KEY, deliberately not a FlowFlags field: clearFlags() runs
 * on every login, and the person following an invite link is usually logging
 * in at that exact moment — a stash inside the flow blob would die at the
 * moment it is needed. The token is a capability, but a bounded one: it grants
 * accepting THIS invitation as the signed-in session, nothing else, and the
 * accept route pairs it with the invite id and the session before it means
 * anything.
 */

const STORAGE_KEY = 'imageshield.invite_token';

/** The backend's token shape (see src/household/tokens.ts): 43 base64url
 *  chars. Not imported — web/ is quarantined from the backend's source. */
const TOKEN_RE = /^[A-Za-z0-9_-]{43}$/;

/**
 * Stash the token when the URL is an invite link. Returns whether it was one —
 * the caller rewrites the URL to the invites screen on true, even if storage
 * was blocked and the prefill will be lost (the right screen with an empty
 * field still beats the sign-in screen with no explanation).
 */
export function captureInviteLink(url: { pathname: string; search: string }): boolean {
  if (!/^\/invite\/?$/.test(url.pathname)) return false;
  const token = new URLSearchParams(url.search).get('token');
  if (token === null || !TOKEN_RE.test(token)) return false;
  try {
    window.localStorage.setItem(STORAGE_KEY, token);
  } catch {
    // Blocked storage loses the prefill, never the navigation.
  }
  return true;
}

export function pendingInviteToken(): string | null {
  try {
    const token = window.localStorage.getItem(STORAGE_KEY);
    return token !== null && TOKEN_RE.test(token) ? token : null;
  } catch {
    return null;
  }
}

/** After a successful accept — the capability is spent with the invite. */
export function clearInviteToken(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear is fine.
  }
}

/**
 * Where "done" lands: the invites screen while an invite link is waiting,
 * the dashboard otherwise. Used by every path that would go home — the
 * post-login resume, the splash gate, and the wizard's finish.
 */
export function homeHash(): '#/home' | '#/invites' {
  return pendingInviteToken() !== null ? '#/invites' : '#/home';
}
